/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { Module, Node, INodeProps } from '@msdyn365-commerce-modules/utilities';
import * as Msdyn365 from '@msdyn365-commerce/core';
import * as React from 'react';
import MsDyn365 from '@msdyn365-commerce/core';
import { IHelp, IOrderSummary, IPaymentMethods } from '@msdyn365-commerce-modules/order-management/src/modules/order-details/../../common';
import { IGroup } from '@msdyn365-commerce-modules/order-management/src/modules/order-details/../../common/get-groups';
import { ISalesLine } from '@msdyn365-commerce-modules/order-management/src/modules/order-details/../../common/get-sales-line';
import { IOrderDetailsViewProps } from '@msdyn365-commerce-modules/order-management/src/modules/order-details/./order-details';
import { IOrderDetailsProps, IOrderDetailsResources } from '../definition-extensions/order-details.ext.props.autogenerated';
import { AttributeValue } from '@msdyn365-commerce/retail-proxy';
import { IAny, ICoreContext, IGeneric } from '@msdyn365-commerce/core-internal';
import { currencyFormat, dateFormatByLocale, filteredProductAttributeList, formatUrlPath, getOrderStatus } from '../../../utils/helper';
import { IContentBlockConfig } from '../definition-extensions/content-block.ext.props.autogenerated';
import { GridSize } from '../../../modules/krannich-c-search-result-container/krannich-c-search-result-container';
import { getAttributeValuesAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/ProductsDataActions.g';

interface IGroupsExtended {
    groupsProps: INodeProps;
    groups: IGroup[];
    resources: IOrderDetailsResources;
    salesId: React.ReactNode;
    createdDate: React.ReactNode;
    statusValue?: number;
    context?: ICoreContext<IGeneric<IAny>>;
}

interface IGroupExtended {
    context?: ICoreContext<IGeneric<IAny>>;
    group: IGroup;
    resources: IOrderDetailsResources;
    salesId: React.ReactNode;
    createdDate: React.ReactNode;
    statusValue?: number;
}

interface IGroupDeliveryExtended {
    deliveryProps: INodeProps;
    pickupProps: INodeProps;
    showTimeslot: boolean;
    heading: React.ReactNode;
    count: React.ReactNode;
    processing: React.ReactNode;
    trackingInfo: React.ReactNode;
    address: React.ReactNode;
    shippingItemsToYou?: React.ReactNode;
    pickupDateTimeslot?: React.ReactNode;
    shippedStatusCheck?: React.ReactNode;
    statusValue?: number;
}

export const OrderDetailsSalesLine: React.FC<ISalesLine> = ({ salesLineProps, salesLine, buyAgainButton, errors }) => (
    <Node {...salesLineProps}>
        <div className='sales-lines-info-container'>
            <div className='buy-it-again-btn1'> {buyAgainButton}</div>
        </div>
        {errors}
    </Node>
);

export const OrderDetailsGroupDelivery: React.FC<IGroupDeliveryExtended> = ({ deliveryProps, heading, count, statusValue }) => (
    <Node {...deliveryProps}>
        <span className='ms-order-details__group-delivery-heading'>
            <span className='ms-order-details__devliery-type'>{getOrderStatus(statusValue)}</span>
        </span>
        {/* {count} */}
    </Node>
);

export const OrderDetailsGroup: React.FC<IGroupExtended> = ({ group, resources, salesId, context, createdDate, statusValue }) => {
    const { groupProps, delivery, address, salesLinesProps, salesLines } = group;
    const productManufacturerIdentifier = 'Manufacturer';
    const productWeightAttribute = context?.app?.config?.productWeightAttribute || 'Weight (kg)';

    //Configs For Mobile View
    const [isMobile, setIsMobile] = React.useState<boolean>();
    const [attributeValues, setAttributeValues] = React.useState<any>([]);
    const [prodAttrIdentifier, setProdAttrIdentifier] = React.useState<any>('');
    const _updateViewport = (): void => {
        const viewport = _getViewport();
        viewport === 'xs' || viewport === 'sm' ? setIsMobile(true) : setIsMobile(false);
    };
    const _getViewport = (): GridSize => {
        // const { context } = productManufacturarAttribute;

        // Always render in mobile viewport on a mobile device
        if (context?.request && context.request.device && context.request.device.Type === 'Mobile') {
            return 'xs';
        }

        if (MsDyn365.isBrowser && window.innerWidth) {
            const gridSettings = context?.request.gridSettings;
            if (gridSettings) {
                if (gridSettings.xs && window.innerWidth <= gridSettings.xs.w) {
                    return 'xs';
                } else if (gridSettings.sm && window.innerWidth <= gridSettings.sm.w) {
                    return 'sm';
                } else if (gridSettings.md && window.innerWidth <= gridSettings.md.w) {
                    return 'md';
                } else if (gridSettings.lg && window.innerWidth <= gridSettings.lg.w) {
                    return 'lg';
                }
                return 'xl';
            }
        }

        return 'lg';
    };
    React.useEffect(() => {
        _updateViewport();
        if (MsDyn365.isBrowser && window.addEventListener) {
            // eslint-disable-next-line @typescript-eslint/no-floating-promises
            window.addEventListener('resize', _updateViewport);
        }
    }, []);
    React.useEffect(() => {
        const fetchData = async () => {
            let prodAttributeIdentifier = 'Manufacturer';
            if (salesLines.length) {
                const res: { recId: number; manufacturer: AttributeValue | undefined }[] = [];
                for (const salesLine of salesLines) {
                    const productAttributes = await getAttributeValuesAsync(
                        { callerContext: context?.actionContext!, queryResultSettings: {} },
                        salesLine.data.product?.RecordId!,
                        context?.request?.apiSettings?.channelId!,
                        context?.request?.apiSettings?.catalogId!
                    );
                    const productAttributesFiltered = filteredProductAttributeList(
                        productAttributes,
                        context?.request.app.config.kcProductAttribute
                    );
                    const manufacturer = productAttributesFiltered?.find(item => item.identifier === productManufacturerIdentifier);
                    if (manufacturer) {
                        prodAttributeIdentifier = manufacturer?.Name! || '';
                    }
                    res.push({ recId: salesLine.data.product?.RecordId!, manufacturer });
                }
                setProdAttrIdentifier(prodAttributeIdentifier);
                setAttributeValues(res);
            }
        };
        fetchData();
    }, [salesLines]);
    //Ending configuration//

    const createdDateJSX = createdDate as JSX.Element;
    const createdDateJSXProps = createdDateJSX.props as any;
    const createdDateJSXPropsChildren = createdDateJSXProps?.children as any;
    const customDate = createdDateJSXPropsChildren[2];
    // Split the date string
    const dateParts = customDate?.split('/');
    // Create a Date object using the parts
    const formattedDate = new Date(dateParts?.[2], dateParts?.[0] - 1, dateParts?.[1]);
    // Format the date as "DD/MM/YYYY" using toLocaleDateString()
    const formattedDateString = formattedDate.toLocaleDateString('en-GB');
    const usSiteFlag = context?.actionContext?.requestContext?.apiSettings?.oun === context?.app.config.ounNumberForUsSite;
    const dateLocale = context?.app.config.dateFormat;
    const currency = context?.request.channel?.Currency;
    const formattedDateStringBylocale =
        (dateLocale && currency && formattedDateString && dateFormatByLocale(dateLocale, currency, formattedDateString)) || undefined;
    if (isMobile) {
        //View Shown in case of Mobile
        return (
            <Node {...groupProps}>
                {salesLines.map(salesLine => {
                    const salesLineAttr = salesLine.data.salesLine.AttributeValues;

                    const productAttrManufactrValue: AttributeValue | undefined = salesLineAttr?.find(
                        (item: AttributeValue) => item.Name === prodAttrIdentifier
                    );
                    const productAttrWeightValue: AttributeValue | undefined = salesLineAttr?.find(
                        (item: AttributeValue) => item.Name === productWeightAttribute
                    );
                    const lineTotal = Number(salesLine?.data?.salesLine?.GrossAmount) - Number(salesLine?.data?.salesLine?.DiscountAmount);
                    const manufItemTypeNameObj = salesLine.data.product?.ExtensionProperties?.find(item => {
                        if (item.Key === 'ManufItemTypeName') {
                            return item;
                        }
                        return null;
                    });

                    const manufacturerObj =
                        attributeValues.length &&
                        attributeValues.find((item: { recId: number | undefined }) => item.recId === salesLine?.data?.product?.RecordId);
                    const urlPath = formatUrlPath(manufacturerObj?.manufacturer?.TextValue, manufItemTypeNameObj?.Value?.StringValue!);
                    const pathName = context?.actionContext.requestContext.url.requestUrl.pathname.split('/');
                    const isAddingPathWithUrl = context?.actionContext.requestContext.app.config.addPathWithUrl ?? false;
                    let productUrl =
                        salesLine &&
                        (isAddingPathWithUrl
                            ? `${context?.actionContext.requestContext.url.requestUrl.origin}/${pathName![1]}/${urlPath}/${
                                  salesLine?.data?.product?.RecordId
                              }.p`
                            : `${context?.actionContext.requestContext.url.requestUrl.origin}/${urlPath}/${salesLine?.data?.product?.RecordId}.p`);
                    const buyAgainButtonCloned = (
                        <a
                            href={productUrl}
                            aria-label={`${resources.buyAgain} ${salesLine.data.product?.Name}`}
                            className='ms-order-details__sales-line-btn-buy-it-again-link'
                        >
                            {resources.buyAgain}
                        </a>
                    );
                    salesLine.buyAgainButton = buyAgainButtonCloned;
                    return (
                        <div className='order-detail-mob-data'>
                            {createdDate && (
                                <span className='order-request-date-container'>
                                    {resources.documentedDateTabelHeaderLabel} <span>: {formattedDateStringBylocale}</span>
                                </span>
                            )}
                            {salesId && (
                                <span className='sales-order-container'>
                                    {resources.salesIDHeaderLabel}
                                    <span>: {salesId}</span>
                                </span>
                            )}
                            {
                                <span className='weight-container'>
                                    {resources.weightTabelHeaderLabel}{' '}
                                    <span>: {productAttrWeightValue?.TextValue ? productAttrWeightValue?.TextValue : '-'}</span>
                                </span>
                            }
                            {
                                <span className='manufacturer-container'>
                                    {/* {resources.manufacturerHeaderLabel} <span>: {productAttrManufactrValue?.TextValue}</span> */}
                                    {prodAttrIdentifier || resources.manufacturerHeaderLabel}{' '}
                                    <span>: {productAttrManufactrValue?.TextValue}</span>
                                </span>
                            }
                            {
                                <span className='product-container'>
                                    {resources.productTabelHeaderLabel} <span>: {salesLine.data.product?.Name}</span>
                                </span>
                            }
                            {
                                <span className='product-number-container'>
                                    {resources.productNumberTabelHeaderLabel}
                                    <span>: {salesLine.data.product?.ItemId}</span>
                                </span>
                            }
                            {
                                <span className='quantity-container'>
                                    {resources.quantityTabelHeaderLabel} <span>: {salesLine.data.salesLine.Quantity}</span>
                                </span>
                            }
                            {
                                <span className='net-amount-container'>
                                    {resources.netAmountTabelHeaderLabel} <span>: {currencyFormat(lineTotal, context)}</span>
                                </span>
                            }
                            {
                                <span className='total-amount-container'>
                                    {resources.grossAmountTabelHeaderLabel}{' '}
                                    <span>: {currencyFormat(salesLine?.data?.salesLine?.TotalAmount, context)}</span>
                                </span>
                            }
                            {
                                <span className='currency-container'>
                                    {resources.currencyTabelHeaderLabel} {usSiteFlag ? <span>: $</span> : <span>: A$</span>}
                                </span>
                            }
                            {createdDate && (
                                <span className='status-container'>
                                    {resources.fulfilledTabelHeaderLabel}{' '}
                                    <span>: {getOrderStatus(salesLine.data.salesLine.SalesStatusValue)}</span>
                                </span>
                            )}
                            <OrderDetailsSalesLine {...salesLine} />
                        </div>
                    );
                })}
            </Node>
        );
    } else {
        //View Shown in case of Web
        return (
            <>
                <Node {...groupProps}>
                    <div className='order-status-heading'>
                        {resources.orderStateLabel}
                        {delivery && <OrderDetailsGroupDelivery {...delivery} statusValue={statusValue} />}
                    </div>
                    <Node className='ms-order-details__sales-lines_container'>
                        {salesLines && (
                            <Node {...salesLinesProps}>
                                <table>
                                    <tr>
                                        <th>{resources.documentedDateTabelHeaderLabel}</th>
                                        <th>{resources.salesIDHeaderLabel}</th>
                                        <th>{resources.weightTabelHeaderLabel}</th>
                                        <th>{prodAttrIdentifier || resources.manufacturerHeaderLabel}</th>
                                        <th>{resources.productTabelHeaderLabel}</th>
                                        <th>{resources.productNumberTabelHeaderLabel}</th>
                                        <th>{resources.quantityTabelHeaderLabel}</th>
                                        <th>{resources.netAmountTabelHeaderLabel}</th>
                                        <th>{resources.grossAmountTabelHeaderLabel}</th>
                                        <th>{resources.currencyTabelHeaderLabel}</th>
                                        <th>{resources.fulfilledTabelHeaderLabel}</th>
                                        <th>{resources.actionTabelHeaderLabel}</th>
                                    </tr>
                                    {salesLines.map(salesLine => {
                                        const salesLineAttr = salesLine.data.salesLine.AttributeValues;

                                        const productAttrManufactrValue: AttributeValue | undefined = salesLineAttr?.find(
                                            (item: AttributeValue) => item.Name === prodAttrIdentifier
                                        );
                                        const productAttrWeightValue: AttributeValue | undefined = salesLineAttr?.find(
                                            (item: AttributeValue) => item.Name === productWeightAttribute
                                        );
                                        const lineTotal =
                                            Number(salesLine?.data?.salesLine?.GrossAmount) -
                                            Number(salesLine?.data?.salesLine?.DiscountAmount);
                                        const manufItemTypeNameObj = salesLine.data.product?.ExtensionProperties?.find(item => {
                                            if (item.Key === 'ManufItemTypeName') {
                                                return item;
                                            }
                                            return null;
                                        });

                                        const manufacturerObj =
                                            attributeValues.length &&
                                            attributeValues.find(
                                                (item: { recId: number | undefined }) => item.recId === salesLine?.data?.product?.RecordId
                                            );
                                        const urlPath = formatUrlPath(
                                            manufacturerObj?.manufacturer?.TextValue ?? '',
                                            manufItemTypeNameObj?.Value?.StringValue! ?? ''
                                        );
                                        const pathName = context?.actionContext.requestContext.url.requestUrl.pathname.split('/');
                                        const isAddingPathWithUrl =
                                            context?.actionContext.requestContext.app.config.addPathWithUrl ?? false;
                                        let productUrl =
                                            salesLine &&
                                            (isAddingPathWithUrl
                                                ? `${context?.actionContext.requestContext.url.requestUrl.origin}/${
                                                      pathName![1]
                                                  }/${urlPath}/${salesLine?.data?.product?.RecordId}.p`
                                                : `${context?.actionContext.requestContext.url.requestUrl.origin}/${urlPath}/${salesLine?.data?.product?.RecordId}.p`);
                                        const buyAgainButtonCloned = (
                                            <a
                                                href={productUrl}
                                                aria-label={`${resources.buyAgain} ${salesLine.data.product?.Name}`}
                                                className='ms-order-details__sales-line-btn-buy-it-again-link'
                                            >
                                                {resources.buyAgain}
                                            </a>
                                        );
                                        salesLine.buyAgainButton = buyAgainButtonCloned;
                                        return (
                                            <tr>
                                                <td>{formattedDateStringBylocale}</td>
                                                <td>{salesId}</td>
                                                <td>{productAttrWeightValue?.TextValue ? productAttrWeightValue?.TextValue : '-'}</td>
                                                <td>{productAttrManufactrValue?.TextValue ? productAttrManufactrValue?.TextValue : '-'}</td>
                                                <td>
                                                    <span className='product-name'>{salesLine.data.product?.Name}</span>
                                                </td>
                                                <td>{salesLine.data.product?.ItemId}</td>
                                                <td>{salesLine.data.salesLine.Quantity}</td>
                                                <td>{currencyFormat(lineTotal, context)}</td>
                                                <td>{currencyFormat(salesLine?.data?.salesLine?.TotalAmount, context)}</td>
                                                {usSiteFlag ? <td>$</td> : <td>A$</td>}
                                                <td> {getOrderStatus(salesLine.data.salesLine.SalesStatusValue)}</td>
                                                <td>
                                                    <OrderDetailsSalesLine {...salesLine} />
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </table>
                            </Node>
                        )}
                        {!delivery.showTimeslot && address}
                        {delivery.showTimeslot && (
                            <Node {...delivery.pickupProps}>
                                {address}
                                {delivery.pickupDateTimeslot}
                            </Node>
                        )}
                    </Node>
                </Node>
            </>
        );
    }
};

export const OrderDetailsGroups: React.FC<IGroupsExtended> = ({
    groupsProps,
    groups,
    resources,
    salesId,
    createdDate,
    statusValue,
    context
}) => (
    <Node {...groupsProps}>
        {groups.map((group: IGroup, index: number) => (
            <OrderDetailsGroup
                key={index}
                group={group}
                resources={resources}
                salesId={salesId}
                createdDate={createdDate}
                statusValue={statusValue}
                context={context}
            />
        ))}
    </Node>
);
interface IOrderSummaryExtend {
    discountAmount?: number;
    context?: ICoreContext<IGeneric<IAny>>;
    deliveryMode?: string;
    chargeAmount?: number;
    resources?: any;
}

interface JSXNumberValue {
    value: number;
}
export const OrderDetailsOrderSummary: React.FC<IOrderSummary & IOrderSummaryExtend> = ({
    orderSummaryProps,
    heading,
    subtotal,
    shipping,
    tax,
    totalAmount,
    earnedPoints,
    discountAmount,
    context,
    deliveryMode,
    chargeAmount,
    resources
}) => {
    const subTotalJSX = subtotal as JSX.Element;
    const subTotalJSXValue = subTotalJSX.props as JSXNumberValue;
    const subTotalAmount = subTotalJSXValue?.value;
    return (
        <Node {...orderSummaryProps}>
            <>
                <h3 className='summary-heading'>{resources?.paymentSummaryText}</h3>
                <div className='summary-wrapper'>
                    {heading}

                    {/* {subtotal} */}
                    <p className='ms-order-details__order-summary-line-subtotal'>
                        <span className='ms-order-details__order-summary-label'>{resources?.subTotalText}</span>
                        <span className='msc-price ms-order-details__order-summary-price'>
                            <span className='msc-price__actual'>{currencyFormat(subTotalAmount, context)}</span>
                        </span>
                    </p>
                    <p className='ms-order-details__order-summary-line-shipping'>
                        <span className='ms-order-details__order-summary-label'>{resources?.shippingFeeText}</span>
                        <span className='msc-price ms-order-details__order-summary-price'>
                            {deliveryMode && deliveryMode === 'Collection' ? (
                                <span className='msc-price__actual' itemProp='price'>
                                    {resources?.freeText}
                                </span>
                            ) : chargeAmount === 0 ? (
                                <div className='to-be-calculated'>
                                    <span className='msc-order-summary__value'>{resources?.toBeCalculatedText}</span>
                                    <div className='tooltip-wrapper' tabIndex={0}>
                                        <span className='tooltip-icon' />
                                        <span className='tooltip-text tooltip-left'>{resources?.excludingFreightCostMessage}</span>
                                    </div>
                                </div>
                            ) : (
                                <span className='msc-price__actual'>{currencyFormat(chargeAmount, context)}</span>
                            )}
                        </span>
                    </p>
                    {tax}
                    {totalAmount}
                    {earnedPoints}
                </div>
            </>
        </Node>
    );
};

export const OrderDetailsPayment: React.FC<IPaymentMethods> = ({ paymentMethodsProps, title, methods }) => (
    <Node {...paymentMethodsProps}>
        {title}
        {methods}
    </Node>
);

export const OrderDetailsHelp: React.FC<IHelp> = ({ helpProps, needHelpLabel, helpLineNumberLabel, contactNumber }) => (
    <Node {...helpProps}>
        {needHelpLabel}
        {helpLineNumberLabel}
        {contactNumber}
    </Node>
);

const OrderDetailsView: React.FC<IOrderDetailsViewProps & IOrderDetailsProps<{}>> = ({
    moduleProps,
    viewModes,
    tableViewActions,
    table,
    placedBy,
    heading,
    alert,
    loading,
    orderInfomation,
    orderSummary,
    groups,
    resources,
    config: { bannerImage, bannerLink },
    context: {
        request: { gridSettings },
        actionContext: { requestContext }
    },
    context,
    data: {
        orderHydration: { result }
    }
}) => {
    const { salesOrder } = result!;

    const imageProps = {
        gridSettings: gridSettings ?? {},
        imageSettings: bannerImage?.imageSettings
    };
    interface IContentBlockFullConfig extends IContentBlockConfig {
        // eslint-disable-next-line @typescript-eslint/naming-convention -- This field comes from SDK.
        msdyn365__moduleLayout?: string;
    }
    const contentBlockImage = (
        <Msdyn365.Image
            {...bannerImage}
            {...imageProps}
            requestContext={requestContext}
            editProps={{
                key: bannerImage ?? {},
                requestContext: requestContext,
                // moduleType: typeName,
                imagePropertyName: 'image',
                // moduleId: props.id,
                layout: (bannerImage as IContentBlockFullConfig).msdyn365__moduleLayout
            }}
            shouldSkipToMainImage
        />
    );
    return (
        <>
            <Module {...moduleProps}>
                {heading}
                {alert}
                {loading}
                {tableViewActions}
                {result?.salesOrder?.Name ? (
                    <div className='ms-order-details__sales-order-placed-by'>
                        {resources.orderPlacedBy} {result?.salesOrder?.Name?.split(' ')[0]}
                    </div>
                ) : (
                    placedBy
                )}
                {viewModes}
                {table}
                {groups && orderInfomation && (
                    <OrderDetailsGroups
                        resources={resources}
                        {...groups}
                        {...orderInfomation}
                        statusValue={result?.salesOrder.StatusValue}
                        context={context}
                    />
                )}
                <Node className='ms-order-details__order-summary-container'>
                    {orderSummary && (
                        <OrderDetailsOrderSummary
                            {...orderSummary}
                            discountAmount={salesOrder.DiscountAmount}
                            context={context}
                            deliveryMode={salesOrder.DeliveryMode}
                            chargeAmount={salesOrder.ChargeAmount}
                            resources={resources}
                        />
                    )}
                    {bannerLink && bannerLink.linkUrl && bannerLink.linkUrl.destinationUrl && (
                        <div className='order-details-bannerImage'>
                            {' '}
                            <a
                                href={bannerLink?.linkUrl?.destinationUrl!}
                                target={bannerLink.openInNewTab ? '_blank' : ''}
                                aria-label={bannerLink?.ariaLabel!}
                            >
                                {contentBlockImage}
                            </a>
                        </div>
                    )}
                    {/* {payment && <OrderDetailsPayment {...payment} />} */}
                </Node>
            </Module>
        </>
    );
};

export default OrderDetailsView;
